import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { CheckCircle2, ChevronRight, ClipboardList, X } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { httpClient } from '../../../common/httpclient';

/**
 * Center modal — DESIGN-SYSTEM.md §5.6 (scrim + 20px box + header
 * ícone-badge/título/descrição + X + corpo rolável + rodapé com ação à
 * direita), mesmo padrão de `ChecklistHistoryModal.js` e
 * `pages/cashregisters/components/CenterModal.js`.
 */
export default function ChecklistSelectionModal({ open, onClose, onSelectChecklist, serviceOrderId }) {
    const [checklists, setChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filledChecklistId, setFilledChecklistId] = useState(null);

    useEffect(() => {
        if (open) {
            fetchChecklists();
            checkFilledChecklist();
        }
    }, [open, serviceOrderId]);

    const checkFilledChecklist = async () => {
        if (!serviceOrderId) return;
        
        try {
            const response = await httpClient.get(`/checklist/service-order/${serviceOrderId}`);
            if (response.status === 200 && response.data) {
                setFilledChecklistId(response.data.checklist_id);
            }
        } catch (error) {
            // Se não encontrar checklist preenchido, continua normalmente
            setFilledChecklistId(null);
        }
    };

    const fetchChecklists = async () => {
        try {
            setLoading(true);
            const response = await httpClient.get('/checklist');
            if (response.status === 200 && response.data) {
                setChecklists(response.data);
            }
        } catch (error) {
            console.error('Error fetching checklists:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectChecklist = (checklist) => {
        onSelectChecklist(checklist);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
        >
            {/* header — ícone-badge + título + descrição + X */}
            <Box sx={{
                px: '22px',
                pt: '20px',
                pb: '14px',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1.25,
            }}>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        background: (t) => t.palette.gradient.brand,
                        mb: '10px',
                    }}>
                        <ClipboardList size={18} strokeWidth={2} />
                    </Box>
                    <Typography sx={{ fontSize: '17px', fontWeight: 800, mb: '4px' }}>
                        Selecionar checklist
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                        Escolha um checklist para preencher
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="Fechar">
                    <X size={15} strokeWidth={2} />
                </IconButton>
            </Box>

            {/* body — corpo rolável */}
            <Box sx={{ p: '18px 22px', maxHeight: '62vh', overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                        <CircularProgress size={30} />
                    </Box>
                ) : checklists.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, textAlign: 'center', py: 4 }}>
                        <Box sx={{ width: 64, height: 64, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                            <ClipboardList size={26} strokeWidth={2} />
                        </Box>
                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.secondary' }}>
                            Nenhum checklist disponível
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        {checklists.map((checklist) => {
                            const isFilled = String(filledChecklistId) === String(checklist.id);
                            return (
                                <ButtonBase
                                    key={checklist.id}
                                    onClick={() => handleSelectChecklist(checklist)}
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        gap: 1.5,
                                        p: 1.75,
                                        textAlign: 'left',
                                        borderRadius: '14px',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        transition: 'border-color .15s, background-color .15s',
                                        '&:hover': { borderColor: 'primary.main', bgcolor: 'background.subtle' },
                                    }}
                                >
                                    <Box sx={{
                                        width: 38,
                                        height: 38,
                                        borderRadius: '11px',
                                        flexShrink: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: isFilled ? 'success.bg' : 'info.bg',
                                        color: isFilled ? 'success.main' : 'primary.main',
                                    }}>
                                        {isFilled ? <CheckCircle2 size={18} strokeWidth={2} /> : <ClipboardList size={18} strokeWidth={2} />}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>
                                                {checklist.name}
                                            </Typography>
                                            {isFilled && (
                                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '20px', bgcolor: 'success.bg', color: 'success.main' }}>
                                                    <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
                                                    Preenchido
                                                </Box>
                                            )}
                                        </Box>
                                        {checklist.description && (
                                            <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: '2px' }}>
                                                {checklist.description}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ color: 'text.disabled', display: 'flex', flexShrink: 0 }}>
                                        <ChevronRight size={16} strokeWidth={2} />
                                    </Box>
                                </ButtonBase>
                            );
                        })}
                    </Box>
                )}
            </Box>

            {/* footer — ação à direita */}
            <Box sx={{
                p: '16px 22px 20px',
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 1.25,
                justifyContent: 'flex-end',
            }}>
                <Button variant="outlined" onClick={onClose}>Cancelar</Button>
            </Box>
        </Dialog>
    );
}

